import type { AuthContextProps } from "react-oidc-context";
import type { SigninRedirectArgs, SignoutRedirectArgs } from "oidc-client-ts";

// ─── Type Definitions ────────────────────────────────────────────────

interface JwtPayload {
  exp: number;
  iat?: number;
  sub?: string;
  [key: string]: unknown;
}

interface KeycloakRealmAccess {
  roles: string[];
}

interface KeycloakResourceAccess {
  [resource: string]: {
    roles: string[];
  };
}

interface KeycloakProfile {
  sub?: string;
  realm_access?: KeycloakRealmAccess;
  resource_access?: KeycloakResourceAccess;
  [key: string]: unknown;
}

export interface KeycloakLikeObject {
  authenticated: boolean;
  profile: KeycloakProfile | Record<string, unknown>;
  token: string | null;
  refreshToken: string | null;
  idToken: string | null;
  tokenParsed: JwtPayload | null;
  refreshTokenParsed: JwtPayload | null;
  idTokenParsed: JwtPayload | null;
  subject: string | null;
  sessionId: string | null;
  realm: string | null;
  login: (options?: SigninRedirectArgs) => Promise<void>;
  logout: (options?: SignoutRedirectArgs) => Promise<void>;
  register: (options?: Record<string, unknown>) => void;
  accountManagement: () => void;
  updateToken: (minValidity?: number) => Promise<boolean>;
  isTokenExpired: (minValidity?: number) => boolean;
  hasRealmRole: (role: string) => boolean;
  hasResourceRole: (role: string, resource: string) => boolean;
  loadUserProfile: () => Promise<Record<string, unknown>>;
  loadUserInfo: () => Promise<Record<string, unknown>>;
  getToken: () => string | null;
  getRefreshToken: () => string | null;
  getIdToken: () => string | null;
  onAuthSuccess: (() => void) | null;
  onAuthError: ((error: Error) => void) | null;
  onAuthRefreshSuccess: (() => void) | null;
  onAuthRefreshError: ((error: Error) => void) | null;
  onAuthLogout: (() => void) | null;
  onTokenExpired: (() => void) | null;
  loading: boolean;
  error: AuthContextProps["error"];
}

// ─── Functions ───────────────────────────────────────────────────────

// Create a Keycloak-like object from OIDC context
export const createKeycloakLikeObject = (auth: AuthContextProps): KeycloakLikeObject => {
  const profile = (auth.user?.profile || {}) as KeycloakProfile;

  const keycloakLike: KeycloakLikeObject = {
    // Authentication status
    authenticated: auth.isAuthenticated,

    // User profile information
    profile,

    // Token information
    token: auth.user?.access_token || null,
    refreshToken: auth.user?.refresh_token || null,
    idToken: auth.user?.id_token || null,

    // Token expiration
    tokenParsed: auth.user?.access_token ? parseJwt(auth.user.access_token) : null,
    refreshTokenParsed: auth.user?.refresh_token ? parseJwt(auth.user.refresh_token) : null,
    idTokenParsed: auth.user?.id_token ? parseJwt(auth.user.id_token) : null,

    // User information
    subject: auth.user?.profile?.sub || null,

    // Session state
    sessionId: auth.user?.session_state || null,

    // Realm information
    realm: extractRealmFromAuthority(auth.settings?.authority),

    // Methods
    login: (options: SigninRedirectArgs = {}): Promise<void> => {
      return auth.signinRedirect(options);
    },

    logout: (options: SignoutRedirectArgs = {}): Promise<void> => {
      localStorage.removeItem("userWasAuthenticated");
      return auth.signoutRedirect(options);
    },

    register: (_options: Record<string, unknown> = {}): void => {
      // For registration, you might need to redirect to Keycloak registration
      const registrationUrl = `${auth.settings?.authority}/protocol/openid-connect/registrations`;
      window.location.href = registrationUrl;
    },

    accountManagement: (): void => {
      const accountUrl = `${auth.settings?.authority}/account`;
      window.open(accountUrl, "_blank");
    },

    updateToken: (minValidity: number = 5): Promise<boolean> => {
      return new Promise((resolve, reject) => {
        if (!auth.user) {
          reject(new Error("No user authenticated"));
          return;
        }

        // Check if token needs refresh
        const tokenParsed: JwtPayload | null = parseJwt(auth.user.access_token);
        if (!tokenParsed) {
          reject(new Error("Failed to parse access token"));
          return;
        }

        const now: number = Math.floor(Date.now() / 1000);
        const expiresIn: number = tokenParsed.exp - now;

        if (expiresIn < minValidity) {
          // Token needs refresh
          auth
            .signinSilent()
            .then(() => resolve(true))
            .catch(reject);
        } else {
          resolve(false); // Token is still valid
        }
      });
    },

    isTokenExpired: (minValidity: number = 0): boolean => {
      if (!auth.user?.access_token) {
        return true;
      }

      const tokenParsed: JwtPayload | null = parseJwt(auth.user.access_token);
      if (!tokenParsed) {
        return true;
      }

      const now: number = Math.floor(Date.now() / 1000);
      const expiresIn: number = tokenParsed.exp - now;

      return expiresIn < minValidity;
    },

    hasRealmRole: (role: string): boolean => {
      return profile.realm_access?.roles?.includes(role) || false;
    },

    hasResourceRole: (role: string, resource: string): boolean => {
      return profile.resource_access?.[resource]?.roles?.includes(role) || false;
    },

    loadUserProfile: (): Promise<Record<string, unknown>> => {
      return Promise.resolve(auth.user?.profile || {});
    },

    loadUserInfo: (): Promise<Record<string, unknown>> => {
      return Promise.resolve(auth.user?.profile || {});
    },

    // Additional utility methods
    getToken: (): string | null => auth.user?.access_token || null,
    getRefreshToken: (): string | null => auth.user?.refresh_token || null,
    getIdToken: (): string | null => auth.user?.id_token || null,

    // Event handlers (for compatibility)
    onAuthSuccess: null,
    onAuthError: null,
    onAuthRefreshSuccess: null,
    onAuthRefreshError: null,
    onAuthLogout: null,
    onTokenExpired: null,

    // Loading states
    loading: auth.isLoading,
    error: auth.error,
  };

  return keycloakLike;
};

// Helper function to parse JWT
const parseJwt = (token: string): JwtPayload | null => {
  try {
    const base64Url: string = token.split(".")[1];
    const base64: string = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload: string = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c: string): string {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(""),
    );

    return JSON.parse(jsonPayload) as JwtPayload;
  } catch (e) {
    return null;
  }
};

// Helper function to extract realm from authority URL
const extractRealmFromAuthority = (authority: string | undefined): string | null => {
  if (!authority) {
    return null;
  }
  const match: RegExpMatchArray | null = authority.match(/\/realms\/([^\/]+)/);
  return match ? match[1] : null;
};
